package com.sweetlime.bingeamigo.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.ActivityUserContext
import com.sweetlime.bingeamigo.model.LibraryMovie
import com.sweetlime.bingeamigo.model.LibraryTvShow
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.model.types.ActivityType
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@Composable
fun UserActivityCard(
    activity: UserActivity,
    modifier: Modifier = Modifier,
    showUsername: Boolean = true,
    userContext: ActivityUserContext? = null,
    onClick: ((UserActivity) -> Unit)? = null
) {
    val hint: ActivityHint? = userContext?.let { computeActivityHint(activity, it) }
    val isTv = activity.tvShowId != 0
    val posterPath = if (isTv) activity.tvShowPoster else activity.moviePoster
    val title = if (isTv) activity.tvShowTitle else activity.movieTitle
    val posterUrl = "https://image.tmdb.org/t/p/w92$posterPath"

    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 6.dp)
            .clip(RoundedCornerShape(18.dp))
            .background(BrandColors.Card)
            .border(1.dp, Color(0xFF2A2A2E), RoundedCornerShape(18.dp))
            .clickable(enabled = onClick != null) { onClick?.invoke(activity) }
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        KamelImage(
            resource = asyncPainterResource(posterUrl),
            contentDescription = title,
            contentScale = ContentScale.Crop,
            modifier = Modifier
                .size(width = 52.dp, height = 78.dp)
                .clip(RoundedCornerShape(8.dp))
                .background(Color(0xFF2A2A2A))
        )

        Spacer(modifier = Modifier.width(12.dp))

        Column(modifier = Modifier.weight(1f)) {
            if (showUsername) {
                Text(
                    text = activity.username,
                    color = Color.White,
                    fontWeight = FontWeight.Bold,
                    fontSize = 15.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(2.dp))
            }
            Text(
                text = activityLabel(activity.type, activity.seasonNumber, activity.episodeNumber, activity.rating),
                color = activityLabelColor(activity.type),
                fontSize = 12.5.sp,
                fontWeight = FontWeight.Bold
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = title,
                color = Color.White,
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (hint != null) {
                Spacer(modifier = Modifier.height(4.dp))
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(4.dp))
                        .background(hint.tone.bgColor())
                        .padding(horizontal = 6.dp, vertical = 2.dp)
                ) {
                    Text(
                        text = hint.text,
                        color = Color(0xFFCCCCCC),
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            } else {
                Spacer(modifier = Modifier.height(18.dp))
            }
        }

        Text(
            text = relativeTime(activity.timestamp),
            color = Color.Gray,
            fontSize = 13.sp
        )
    }
}

fun activityLabel(type: Int, seasonNumber: Int = 0, episodeNumber: Int = 0, rating: Double = 0.0): String {
    val ratingStr = if (rating > 0.0) " ★ ${rating.format1f()}" else ""
    return when (ActivityType.fromValue(type)) {
        ActivityType.ADD_WATCHLIST     -> "Added to watchlist"
        ActivityType.MARK_WATCHED      -> "Marked as watched"
        ActivityType.RATE_MOVIE        -> "Rated$ratingStr"
        ActivityType.REVIEW_MOVIE      -> "Reviewed"
        ActivityType.FAVORITE_MOVIE    -> "Added to favorites"
        ActivityType.REWATCH_MOVIE     -> "Rewatched"
        ActivityType.ADD_SHOW_TRACKING -> "Started tracking show"
        ActivityType.FAVORITE_SHOW     -> "Favorited show"
        ActivityType.COMPLETE_SHOW     -> "Completed show"
        ActivityType.EPISODE_WATCHED   -> "Watched S${seasonNumber.toString().padStart(2, '0')}E${episodeNumber.toString().padStart(2, '0')}"
        ActivityType.RATE_SHOW         -> "Rated show$ratingStr"
        ActivityType.RATE_EPISODE      -> "Rated S${seasonNumber.toString().padStart(2, '0')}E${episodeNumber.toString().padStart(2, '0')}$ratingStr"
        else                           -> "Activity"
    }
}

fun activityLabelColor(type: Int): Color = when (ActivityType.fromValue(type)) {
    ActivityType.ADD_WATCHLIST,
    ActivityType.REWATCH_MOVIE,
    ActivityType.ADD_SHOW_TRACKING -> BrandColors.Secondary
    else                           -> BrandColors.Primary
}

enum class HintTone { GREEN, YELLOW, RED }

data class ActivityHint(val text: String, val tone: HintTone)

fun HintTone.bgColor(): Color = when (this) {
    HintTone.GREEN  -> Color(0x4000E676)
    HintTone.YELLOW -> Color(0x40FFB800)
    HintTone.RED    -> Color(0x40FF3333)
}

fun computeActivityHint(activity: UserActivity, context: ActivityUserContext): ActivityHint? {
    val type = ActivityType.fromValue(activity.type) ?: return null
    return if (activity.tvShowId != 0) {
        computeTvHint(activity, type, context.libraryShow)
    } else {
        computeMovieHint(type, context.libraryMovie)
    }
}

private fun computeTvHint(
    activity: UserActivity,
    type: ActivityType,
    userShow: LibraryTvShow?
): ActivityHint? {
    if (userShow == null) return ActivityHint("Not on your list yet", HintTone.YELLOW)

    val userCompleted = userShow.completed
    val userLastWatched = userShow.episodes
        .filter { it.watched }
        .maxWithOrNull(compareBy({ it.seasonNumber }, { it.episodeNumber }))

    return when (type) {
        ActivityType.COMPLETE_SHOW -> when {
            userCompleted            -> ActivityHint("You both finished this", HintTone.YELLOW)
            userLastWatched != null  -> ActivityHint("You're still watching this", HintTone.RED)
            else                     -> ActivityHint("You haven't seen this one", HintTone.YELLOW)
        }
        ActivityType.EPISODE_WATCHED -> when {
            userCompleted -> ActivityHint("You finished this one", HintTone.GREEN)
            else          -> compareEpisodePositions(
                userLastWatched?.seasonNumber, userLastWatched?.episodeNumber,
                activity.seasonNumber, activity.episodeNumber
            )
        }
        ActivityType.REVIEW_MOVIE -> when {
            userCompleted            -> ActivityHint("You finished it too", HintTone.YELLOW)
            userLastWatched != null  -> ActivityHint("You're still watching this", HintTone.RED)
            else                     -> ActivityHint("You haven't seen this one", HintTone.YELLOW)
        }
        else -> null
    }
}

private fun compareEpisodePositions(
    userSeason: Int?, userEpisode: Int?,
    friendSeason: Int, friendEpisode: Int
): ActivityHint {
    if (userSeason == null || userEpisode == null) return ActivityHint("Not on your list yet", HintTone.YELLOW)

    val seasonDiff = userSeason - friendSeason
    return when {
        seasonDiff > 1   -> ActivityHint("You're $seasonDiff seasons ahead", HintTone.GREEN)
        seasonDiff == 1  -> ActivityHint("You're a full season ahead", HintTone.GREEN)
        seasonDiff < -1  -> ActivityHint("They're ${-seasonDiff} seasons ahead of you", HintTone.RED)
        seasonDiff == -1 -> ActivityHint("They're a season ahead of you", HintTone.RED)
        else -> {
            val epDiff = userEpisode - friendEpisode
            val n = kotlin.math.abs(epDiff)
            val s = if (n == 1) "" else "s"
            when {
                epDiff > 0  -> ActivityHint("You're $n episode$s ahead", HintTone.GREEN)
                epDiff < 0  -> ActivityHint("You're $n episode$s behind", HintTone.RED)
                else        -> ActivityHint("You're on the same episode", HintTone.YELLOW)
            }
        }
    }
}

private fun computeMovieHint(type: ActivityType, userMovie: LibraryMovie?): ActivityHint? {
    return when (type) {
        ActivityType.ADD_WATCHLIST -> when {
            userMovie?.watched == true   -> null
            userMovie?.watchlist == true -> ActivityHint("Also on your watchlist", HintTone.YELLOW)
            else                         -> ActivityHint("Not on your list yet", HintTone.YELLOW)
        }
        ActivityType.MARK_WATCHED, ActivityType.REWATCH_MOVIE -> when {
            userMovie?.watched == true   -> ActivityHint("You've seen this one", HintTone.YELLOW)
            userMovie?.watchlist == true -> ActivityHint("They started it, you haven't yet", HintTone.RED)
            else                         -> ActivityHint("Not on your list yet", HintTone.YELLOW)
        }
        ActivityType.RATE_MOVIE, ActivityType.REVIEW_MOVIE -> when {
            userMovie?.watched == true -> ActivityHint("You finished it too", HintTone.YELLOW)
            else                       -> ActivityHint("You haven't seen this one", HintTone.YELLOW)
        }
        ActivityType.FAVORITE_MOVIE -> when {
            userMovie == null -> ActivityHint("Not on your list yet", HintTone.YELLOW)
            else              -> null
        }
        else -> null
    }
}

@OptIn(ExperimentalTime::class)
fun relativeTime(timestamp: Long): String {
    val diff = Clock.System.now().toEpochMilliseconds() - timestamp
    val minutes = diff / 60_000
    val hours = minutes / 60
    val days = hours / 24
    return when {
        minutes < 1  -> "Just now"
        minutes < 60 -> "${minutes}m ago"
        hours < 24   -> "${hours}h ago"
        days < 30    -> "${days}d ago"
        else         -> "${days / 30}mo ago"
    }
}